import { useState } from "react";
import { Badge, Collapse } from "react-bootstrap";
import { Link } from "@tanstack/react-router";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useModuleEnabled } from "../../redux/selectors/modules";
import { useAppSelector } from "../../redux/hooks";
import { GoRocket } from "react-icons/go";

const BISidebar = ({ applications, hideSearchAnalytics = false, title, activeSection, activeColor }) => {
  const { t } = useTranslation(["common"]);
  const hasOrgSummaryReportModule = useModuleEnabled("ORG_SUMMARY_REPORT");
  const hasChatModule = useModuleEnabled("CHAT");

  const resolvedTitle = title ?? t(($) => $.common.sidebar["Marketing Intelligence"]);

  const user = useAppSelector((state) => state.user);
  const channel = useAppSelector((state) => state.channel.currentChannel);
  const [openBI, setOpenBI] = useState(false);

  const isActive = (routeName) => {
    const pathname = document.location.pathname;
    return pathname.endsWith(routeName) || pathname.includes(routeName + "/") ? "active" : "";
  };

  const activeLinkStyles = {
    borderBottom: "1px solid #2e0054",
    borderRadius: 0,
  };

  const now = moment();
  const tosDate = moment(user?.tos_acceptance_date?.slice(0, 10));
  const daysSinceSignup = tosDate ? now.diff(tosDate, "days") : 100;

  if (applications.length === 1 && applications.some((m) => m === "Search Analytics")) {
    const highlightLink = isActive("/user/searchanalytics");

    return (
      <li className={isActive("/user/searchanalytics")} style={{ width: "100%" }}>
        <Link
          to="/user/searchanalytics"
          activeOptions={{ includeSearch: false }}
          className="nav-link"
          style={{
            marginBottom: "3rem",
            cursor: "pointer",
            backgroundColor: highlightLink ? activeColor : "inherit",
            borderBottom: highlightLink ? `1px solid ${activeColor}` : "",
            borderRadius: highlightLink ? "5px" : 0,
          }}
        >
          <i
            className="fa fa-pie-chart"
            style={{
              color: highlightLink ? "#FFF" : "inherit",
            }}
          ></i>
          <p>
            {t(($) => $.common.sidebar["Search Analytics"])}
            {user.tier === "unknown" && user.pricing_tier === "unknown" && daysSinceSignup < 3 && (
              <Badge
                className={`badge-${isActive("/user/searchanalytics") ? "secondary" : "primary"}`}
                style={{ marginLeft: "1rem" }}
                pill
              >
                {t(($) => $.common.sidebar.NEW)}
              </Badge>
            )}
          </p>
        </Link>
      </li>
    );
  }

  return (
    <li
      className="user"
      style={{
        marginBottom: "2rem",
        marginTop: 0,
        paddingBottom: "1rem",
        borderBottom: "none",
        width: "100%",
        textDecoration: "none",
        borderRadius: "15px",
      }}
    >
      <a
        href="#"
        className="active"
        onClick={(e) => {
          e.preventDefault();
          setOpenBI(!openBI);
        }}
        style={{
          backgroundColor: activeSection === "reports" ? "#2e0054" : "inherit",
        }}
      >
        <i className="pe-7s-news-paper" style={{ color: activeSection === "reports" ? "#FFF" : "#2e0054" }}></i>
        <p style={{ color: activeSection === "reports" ? "#FFF" : "#2e0054" }}>
          {resolvedTitle}
          <span className={`caret ${openBI && `rotate-180`} dropdown-toggle`}></span>
        </p>
      </a>

      <Collapse in={openBI}>
        <ul className="nav">
          {!hideSearchAnalytics && (
            <li className={isActive("searchanalytics")} style={{ width: "100%" }}>
              <Link
                to="/user/searchanalytics"
                activeProps={{}}
                style={isActive("searchanalytics") ? activeLinkStyles : {}}
              >
                <span className="sidebar-mini">
                  <i className="pe-7s-graph" />
                </span>
                <span className="sidebar-normal sidebar-link">
                  {t(($) => $.common.sidebar["Search Analytics"])}{" "}
                  {user.tier === "unknown" && user.pricing_tier === "unknown" && daysSinceSignup < 3 && (
                    <Badge
                      className={`badge-${isActive("/user/searchanalytics") ? "secondary" : "primary"}`}
                      style={{ marginLeft: ".5rem" }}
                      pill
                    >
                      {t(($) => $.common.sidebar.NEW)}
                    </Badge>
                  )}
                </span>
              </Link>
            </li>
          )}

          {applications.find((x) => x === "Advertising") && (
            <>
              {channel !== "shopify" && hasChatModule && (
                <li className={isActive("tclip/agents")} style={{ width: "100%" }}>
                  <Link
                    to="/user/tclip/agents"
                    activeProps={{}}
                    style={isActive("tclip/agents") ? activeLinkStyles : {}}
                  >
                    <span className="sidebar-mini" style={{ fontSize: "2rem", paddingRight: "1rem" }}>
                      <GoRocket />
                    </span>
                    <span className="sidebar-normal sidebar-link">
                      {t(($) => $.common.sidebar["Report Agents"])}
                      <Badge
                        className={`badge-${isActive("tclip/agents") ? "secondary" : "primary"}`}
                        style={{ marginLeft: "0.5rem", fontSize: "9px" }}
                        pill
                      >
                        {t(($) => $.common.sidebar.BETA)}
                      </Badge>
                    </span>
                  </Link>
                </li>
              )}

              <li className={isActive("report/product")} style={{ width: "100%" }}>
                <Link
                  to="/user/report/product"
                  activeProps={{}}
                  style={isActive("/user/report/product") ? activeLinkStyles : {}}
                >
                  <span className="sidebar-mini">
                    <i className="fa fa-archive" />
                  </span>
                  <span className="sidebar-normal sidebar-link">{t(($) => $.common.sidebar["Product Report"])}</span>
                </Link>
              </li>

              {channel !== "shopify" && (
                <li className={isActive("report/product_ad")} style={{ width: "100%" }}>
                  <Link
                    to="/user/report/product_ad"
                    activeProps={{}}
                    style={isActive("/user/report/product_ad") ? activeLinkStyles : {}}
                  >
                    <span className="sidebar-mini">
                      <i className="fa fa-cube" />
                    </span>
                    <span className="sidebar-normal sidebar-link">
                      {t(($) => $.common.sidebar["Product Ad Report"])}
                    </span>
                  </Link>
                </li>
              )}

              {channel !== "shopify" && (
                <li className={isActive("report/advertisement")} style={{ width: "100%" }}>
                  <Link
                    to="/user/report/advertisement"
                    activeProps={{}}
                    style={isActive("/user/report/advertisement") ? activeLinkStyles : {}}
                  >
                    <span className="sidebar-mini">
                      <i className="fa fa-adn" />
                    </span>
                    <span className="sidebar-normal sidebar-link">{t(($) => $.common.sidebar["Ad Report"])}</span>
                  </Link>
                </li>
              )}
              {channel === "amazon" && (
                <>
                  <li className={isActive("report/target")} style={{ width: "100%" }}>
                    <Link
                      to="/user/report/target"
                      activeProps={{}}
                      style={isActive("/user/report/target") ? activeLinkStyles : {}}
                    >
                      <span className="sidebar-mini">
                        <i className="fa fa-bullseye" />
                      </span>
                      <span className="sidebar-normal sidebar-link">{t(($) => $.common.sidebar["Target Report"])}</span>
                    </Link>
                  </li>
                  <li className={isActive("report/audience")} style={{ width: "100%" }}>
                    <Link
                      to="/user/report/audience"
                      activeProps={{}}
                      style={isActive("/user/report/audience") ? activeLinkStyles : {}}
                    >
                      <span className="sidebar-mini">
                        <i className="fa fa-street-view" />
                      </span>
                      <span className="sidebar-normal sidebar-link">
                        {t(($) => $.common.sidebar["Audience Report"])}
                      </span>
                    </Link>
                  </li>
                  <li className={isActive("report/str_report")} style={{ width: "100%" }}>
                    <Link
                      to={"/user/report/str_report"}
                      activeProps={{}}
                      style={isActive("/user/report/str_report") ? activeLinkStyles : {}}
                    >
                      <span className="sidebar-mini">
                        <i className="fa fa-search" />
                      </span>
                      <span className="sidebar-normal sidebar-link">
                        {t(($) => $.common.sidebar["Search Term Report"])}
                      </span>
                    </Link>
                  </li>
                  {user.organization_type === "seller" && (
                    <li className={isActive("report/sqpr_report")} style={{ width: "100%" }}>
                      <Link
                        to="/user/report/sqpr_report"
                        activeProps={{}}
                        style={isActive("/user/report/sqpr_report") ? activeLinkStyles : {}}
                      >
                        <span className="sidebar-mini">
                          <i className="fa fa-book" />
                        </span>
                        <span className="sidebar-normal sidebar-link">
                          {t(($) => $.common.sidebar["Search Query Perf"])}
                        </span>
                      </Link>
                    </li>
                  )}
                </>
              )}
              {hasOrgSummaryReportModule && (
                <li className={isActive("report/organization/summary")} style={{ width: "100%" }}>
                  <Link
                    to="/user/report/organization/summary"
                    activeProps={{}}
                    style={isActive("/user/report/organization/summary") ? activeLinkStyles : {}}
                  >
                    <span className="sidebar-mini">
                      <i className="fa fa-list" />
                    </span>
                    <span className="sidebar-normal sidebar-link">{t(($) => $.common.sidebar["Summary Report"])}</span>
                  </Link>
                </li>
              )}
            </>
          )}

          <li className={isActive("report/export")} style={{ width: "100%" }}>
            <Link
              to="/user/report/export"
              activeProps={{}}
              style={isActive("/user/report/export") ? activeLinkStyles : {}}
            >
              <span className="sidebar-mini">
                <i className="fa fa-download" />
              </span>
              <span className="sidebar-normal sidebar-link">{t(($) => $.common.sidebar.Exports)}</span>
            </Link>
          </li>
        </ul>
      </Collapse>
    </li>
  );
};

export default BISidebar;
